import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import { ROLE, scheduleStatus } from "../../../../constants";
import { currencySuffix, getErrorMessage } from "../../../../common";
import { ahoraCdmx, hoyCdmx } from "../../../../utils/horaMexico";
import { CoursePlanDTO } from "../../../../models/CoursePlanDTO";
import { BuscadorBarra, coincide } from "../../../../components/buscadorBarra/buscadorBarra";
import AvisoChoque from "./horarioFormAviso";
import CambiarHora from "./cambiarHora";
import {
	AulaOpcion, Conflicto, CursoCat, DIAS_CORTOS, DIAS_LARGOS, HorarioCargado, NivelCat, PERMITIR_CHOQUE_SUPERADMIN, Periodo, Sesion, TIEMPOS, Validacion,
	aHHMM, cargarAulas, cargarCatalogo, cargarHorario, cargarPeriodos, cargarPlan, fechaCorta, guardar, hora12, horaTexto, mensajeServidor, minutos, ocupacion, validar
} from "./horarioFormApi";
import "./horarioForm.css";

// Alta y edición de un horario en 3 pasos (Horarios Claros). La lista lo monta con estas props.
export interface HorarioFormProps {
	show: boolean;
	horarioId?: number | null;   // null = Nuevo Horario
	nivelId?: number | null;     // módulo ya elegido (desde Cursos o desde un hueco de Ocupación de Aulas)
	aulaId?: number | null;
	weekDay?: number | null;     // 0 = Lunes
	inicio?: string | null;      // "11:00"
	onGuardado: (id: number) => void;
	onCerrar: () => void;
}

// Los cursos que cobran por horario y no por plan (los mismos de schedulePrompt.tsx); _plan.scheduleBilled manda cuando ya llegó.
const CURSOS_COBRADOS_POR_HORARIO = ["TAL", "TLL", "GADM", "BDB", "BDBT", "AAA5", "TLT75", "WA4", "AA2", "FDGYANNISZELAYA"];
const MODULOS_COBRADOS_POR_HORARIO = ["TAL901"];

type Seccion = string; // "" | "A" | "B" | "C" o la que ya traía el nombre ("1", "2"…)
interface Form {
	cursoId: number | null; nivelId: number | null;
	dias: number[]; entrada: string; salida: string; distintas: boolean; porDia: { [dia: number]: { inicio: string; fin: string } };
	edificioId: number | null; aulaId: number | null; maestraId: number | null; seccion: Seccion;
	periodoId: number | null; inicio: string; meses: string; min: string; max: string;
	inscripcion: string; mensualidad: string; recargoInscripcion: number; recargoMensualidad: number;
	ajuste: string; reposicion: string; inscripcionPropia: string; venceDia: string; tarifa: string; notas: string;
}

const Campo: React.FC<{ t: string; falta?: boolean; ancho?: boolean; children: React.ReactNode }> = ({ t, falta, ancho, children }) => (
	<div className={"hf-campo" + (falta ? " falta" : "") + (ancho ? " ancho" : "")}><label>{t}</label>{children}</div>
);

interface Paso1Props {
	form: Form; cat: CursoCat[]; curso: CursoCat | null; nivel: NivelCat | null; cambiando: boolean; nivelBloqueado: boolean;
	onCambiar: () => void; onCurso: (c: CursoCat) => void; onNivel: (c: CursoCat, n: NivelCat) => void;
}
// Fuera del formulario: definido adentro se vuelve a montar en cada tecla y pierde lo escrito.
const Paso1: React.FC<Paso1Props> = ({ form, cat, curso, nivel, cambiando, nivelBloqueado, onCambiar, onCurso, onNivel }) => {
	const [qCurso, setQCurso] = useState("");
	const [qNivel, setQNivel] = useState("");
	if (form.cursoId && form.nivelId && !cambiando) {
		return (
			<div className="hf-elegido">
				<div><b>{curso?.name}</b><small>{nivel?.name}</small></div>
				{nivelBloqueado ? <small>Ya tiene alumnas: no se puede cambiar el módulo.</small>
					: <button type="button" className="hf-boton" onClick={onCambiar}>Cambiar</button>}
			</div>
		);
	}
	const cursos = cat.filter((c) => c.niveles.length > 0 && coincide(qCurso, c.name, c.code));
	const niveles = (curso?.niveles || []).filter((n) => coincide(qNivel, n.name, n.code));
	const rotulo: React.CSSProperties = { position: "static", fontSize: 13.5, fontWeight: 600, color: "var(--wa-apagado)", margin: "0 0 6px", display: "block" };
	return (
		<div className="hf-dos">
			<div>
				<label style={rotulo}>Curso</label>
				<BuscadorBarra valor={qCurso} onBuscar={setQCurso} placeholder="Buscar Curso" />
				<div className="hf-lista">
					{cursos.length < 1 ? <div className="hf-vacio">No hay cursos con ese nombre.</div> : cursos.map((c) => (
						<button type="button" key={c.id} className={"hf-item" + (c.id === form.cursoId ? " on" : "")} onClick={() => onCurso(c)}>
							{c.name}<small>{c.code}</small>
						</button>
					))}
				</div>
			</div>
			<div>
				<label style={rotulo}>Módulo</label>
				<BuscadorBarra valor={qNivel} onBuscar={setQNivel} placeholder="Buscar Módulo" />
				<div className="hf-lista">
					{!curso ? <div className="hf-vacio">Primero elige el curso.</div>
						: niveles.length < 1 ? <div className="hf-vacio">No hay módulos con ese nombre.</div>
							: niveles.map((n) => (
								<button type="button" key={n.id} className={"hf-item" + (n.id === form.nivelId ? " on" : "")} onClick={() => onNivel(curso, n)}>
									{n.name}<small>{n.code}</small>
								</button>
							))}
				</div>
			</div>
		</div>
	);
};

const PASOS = [
	{ key: "curso", titulo: "Curso y Módulo", icono: solid("book-open") },
	{ key: "horas", titulo: "Días, Hora y Aula", icono: solid("clock") },
	{ key: "precio", titulo: "Fechas y Precio", icono: solid("calendar-days") }
];

// Inicio por omisión de un periodo: su primer día, o hoy si ese periodo es el mes en curso (no arrancar en el pasado).
const inicioDe = (p: Periodo): string => { const hoy = hoyCdmx(); return p.start < hoy && p.start.slice(0, 7) === hoy.slice(0, 7) ? hoy : p.start; };
const num = (t: string): number => (t.trim() === "" ? NaN : Number(t));

const HorarioFormInterior: React.FC<HorarioFormProps> = ({ horarioId, nivelId, aulaId, weekDay, inicio, onGuardado, onCerrar }) => {
	const campusId: number = useSelector((s: RootState) => s.currentUserCampusId.value) as any;
	const campusRef: any = useSelector((s: RootState) => s.campusRef.value);
	const campusActual: any = useSelector((s: RootState) => s.currentUserCampus.value);
	const perfil: any = useSelector((s: RootState) => s.userProfile.value);
	const esSuper: boolean = PERMITIR_CHOQUE_SUPERADMIN && perfil?.currentRole === ROLE.SUPER_ADMIN;
	const superAdmin: boolean = perfil?.currentRole === ROLE.SUPER_ADMIN;
	const moneda: any = campusRef?.[campusActual]?.company?.currency || { symbol: "$", code: "" };
	const sufijo: string = currencySuffix[(campusRef?.[campusActual]?.company?.currency_id || 1) - 1] || "_mxn";
	const dinero = (v: number): string => moneda.symbol + (isNaN(v) ? 0 : v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	const editando = horarioId != null;
	const [cambiandoHora, setCambiandoHora] = useState(false);

	// ---------------------------------------------------------------- datos que llegan
	const [cargando, setCargando] = useState(true);
	const [errorCarga, setErrorCarga] = useState<string | null>(null);
	const [cat, setCat] = useState<CursoCat[]>([]);
	const [aulas, setAulas] = useState<AulaOpcion[]>([]);
	const [periodos, setPeriodos] = useState<Periodo[]>([]);
	const [orig, setOrig] = useState<HorarioCargado | null>(null);
	const [plan, setPlan] = useState<CoursePlanDTO | null>(null);
	const [form, setForm] = useState<Form | null>(null);

	// ---------------------------------------------------------------- navegación
	const [paso, setPaso] = useState(0);
	const [visitados, setVisitados] = useState<number[]>([0]);
	const [atras, setAtras] = useState(false);
	const [cambiandoCurso, setCambiandoCurso] = useState(false);
	const cuerpo = useRef<HTMLDivElement | null>(null);

	// ---------------------------------------------------------------- validación en vivo y guardado
	const [val, setVal] = useState<Validacion | null>(null);
	const [validando, setValidando] = useState(false);
	const [validacionCaida, setValidacionCaida] = useState(false);
	const [libres, setLibres] = useState<AulaOpcion[] | null>(null);
	const [cargandoLibres, setCargandoLibres] = useState(false);
	const [errorLibres, setErrorLibres] = useState<string | null>(null);
	const [forzar, setForzar] = useState<string | null>(null);
	const [guardando, setGuardando] = useState(false);
	const [errorGuardar, setErrorGuardar] = useState<string | null>(null);
	const [conflictosServidor, setConflictosServidor] = useState<Conflicto[]>([]);

	const periodosElegibles = useMemo(() => {
		const hoy = ahoraCdmx();
		const d2 = (n: number) => String(n).padStart(2, "0");
		const desde = hoy.getFullYear() + "-" + d2(hoy.getMonth() + 1) + "-01";
		const hasta = (hoy.getFullYear() + 1) + "-" + d2(hoy.getMonth() + 1) + "-" + d2(hoy.getDate());
		return periodos.filter((p) => (orig?.periodoId != null && p.id === orig.periodoId) || (p.start !== "" && p.start >= desde && p.start <= hasta))
			.sort((a, b) => a.start.localeCompare(b.start));
	}, [periodos, orig]);

	// ---------------------------------------------------------------- carga inicial
	useEffect(() => {
		let vigente = true;
		(async () => {
			try {
				const [c, a, p, h] = await Promise.all([
					cargarCatalogo(campusId, sufijo), cargarAulas(campusId), cargarPeriodos(campusId),
					editando ? cargarHorario(campusId, horarioId as number) : Promise.resolve(null)
				]);
				if (!vigente) return;
				setCat(c); setAulas(a); setPeriodos(p); setOrig(h);

				let nivel: NivelCat | null = null, curso: CursoCat | null = null;
				const buscarNivel = h ? h.nivelId : (nivelId || null);
				if (buscarNivel) c.forEach((cc) => cc.niveles.forEach((n) => { if (n.id === buscarNivel) { nivel = n; curso = cc; } }));
				const aulaInicial = h ? h.aulaId : (aulaId || null);
				const entrada = h?.sesiones[0]?.inicio || inicio || "09:00";
				const salidaDef = aHHMM(Math.min(minutos(entrada) + 180, 1320));
				const sesion0 = h?.sesiones[0];
				const porDia: Form["porDia"] = {};
				(h?.sesiones || []).forEach((s) => { porDia[s.dia] = { inicio: s.inicio, fin: s.fin }; });
				const distintas = !!h && h.sesiones.some((s) => s.inicio !== sesion0!.inicio || s.fin !== sesion0!.fin);
				const hoy = ahoraCdmx();
				const hoyTxt = hoy.getFullYear() + "-" + String(hoy.getMonth() + 1).padStart(2, "0") + "-01";
				const primero = p.filter((x) => x.start >= hoyTxt).sort((x, y) => x.start.localeCompare(y.start))[0] || null;
				const base = (nivel as NivelCat | null)?.base;
				setForm({
					cursoId: curso ? (curso as CursoCat).id : null, nivelId: nivel ? (nivel as NivelCat).id : null,
					dias: h ? Array.from(new Set(h.sesiones.map((s) => s.dia))).sort() : (weekDay != null ? [weekDay] : []),
					entrada, salida: sesion0?.fin || salidaDef, distintas, porDia,
					edificioId: aulaInicial ? (a.find((x) => x.id === aulaInicial)?.edificioId ?? null) : null, aulaId: aulaInicial,
					maestraId: h ? h.maestraId : null, seccion: (h?.seccion || "") as Seccion,
					periodoId: h ? h.periodoId : (primero ? primero.id : null), inicio: h ? h.inicio : (primero ? inicioDe(primero) : ""),
					meses: h && h.meses != null ? String(h.meses) : "", min: String(h ? h.min : 5), max: String(h ? h.max : 30),
					inscripcion: String(h ? h.inscripcion : (base?.inscripcion ?? 0)), mensualidad: String(h ? h.mensualidad : (base?.mensualidad ?? 0)),
					recargoInscripcion: h ? h.recargoInscripcion : (base?.recargoInscripcion ?? 0), recargoMensualidad: h ? h.recargoMensualidad : (base?.recargoMensualidad ?? 0),
					ajuste: h && h.ajuste != null ? String(h.ajuste) : "", reposicion: h && h.reposicion != null ? String(h.reposicion) : "",
					inscripcionPropia: h && h.inscripcionPropia != null ? String(h.inscripcionPropia) : "", venceDia: String(h ? h.venceDia : 20),
					tarifa: String(h ? h.tarifaMaestra : 0), notas: h?.notas || ""
				});
				if (nivel && !h) { setPaso(1); setVisitados([0, 1]); }
			} catch (e: any) {
				if (vigente) setErrorCarga(e?.response ? getErrorMessage(e) : (e?.message || "No se pudo cargar el formulario."));
			} finally {
				if (vigente) setCargando(false);
			}
		})();
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const curso: CursoCat | null = useMemo(() => cat.find((c) => c.id === form?.cursoId) || null, [cat, form?.cursoId]);
	const nivel: NivelCat | null = useMemo(() => curso?.niveles.find((n) => n.id === form?.nivelId) || null, [curso, form?.nivelId]);

	// El plan del curso dice si se cobra por horario y cuántos meses dura.
	useEffect(() => {
		setPlan(null);
		if (!form?.cursoId) return;
		let vigente = true;
		cargarPlan(campusId, form.cursoId).then((p) => { if (vigente) setPlan(p); });
		return () => { vigente = false; };
	}, [campusId, form?.cursoId]);

	const cobraPorHorario: boolean = plan ? !!plan.scheduleBilled : (CURSOS_COBRADOS_POR_HORARIO.includes(curso?.code || "") || MODULOS_COBRADOS_POR_HORARIO.includes(nivel?.code || ""));
	const nivelBloqueado = editando && !!orig && (orig.tieneMatriculas || orig.tieneListas);
	const horasBloqueadas = editando && !!orig && orig.tieneListas;
	const periodoBloqueado = editando && !!orig && (orig.tieneListas || !superAdmin);
	const inicioBloqueado = editando && !!orig && orig.tieneListas;

	const cambiar = (parche: Partial<Form>) => setForm((f) => (f ? { ...f, ...parche } : f));

	// Al elegir otro módulo: precios base del módulo (solo en alta o si cambió) y la maestra se queda solo si da ese módulo.
	const elegirNivel = (c: CursoCat, n: NivelCat) => {
		setForm((f) => {
			if (!f) return f;
			const mismo = f.nivelId === n.id;
			const maestraOk = f.maestraId != null && n.mentores.some((m) => m.id === f.maestraId);
			return {
				...f, cursoId: c.id, nivelId: n.id, maestraId: maestraOk ? f.maestraId : null,
				...(mismo || (editando && orig && n.id === orig.nivelId) ? {} : {
					inscripcion: String(n.base.inscripcion), mensualidad: String(n.base.mensualidad),
					recargoInscripcion: n.base.recargoInscripcion, recargoMensualidad: n.base.recargoMensualidad
				})
			};
		});
		setCambiandoCurso(false);
	};

	// ---------------------------------------------------------------- sesiones
	const sesiones: Sesion[] = useMemo(() => {
		if (!form) return [];
		return [...form.dias].sort().map((d) => {
			const pd = form.distintas ? form.porDia[d] : null;
			return { dia: d, inicio: pd ? pd.inicio : form.entrada, fin: pd ? pd.fin : form.salida };
		});
	}, [form]);
	const horasMalas = sesiones.some((s) => minutos(s.fin) <= minutos(s.inicio));
	const sesionesOk = sesiones.length > 0 && !horasMalas;
	const sesionesClave = JSON.stringify(sesiones);

	const alternarDia = (d: number) => {
		if (!form) return;
		const esta = form.dias.includes(d);
		const porDia = { ...form.porDia };
		if (!esta && !porDia[d]) porDia[d] = { inicio: form.entrada, fin: form.salida };
		cambiar({ dias: esta ? form.dias.filter((x) => x !== d) : [...form.dias, d], porDia });
	};
	const alternarDistintas = () => {
		if (!form) return;
		if (!form.distintas) {
			const porDia = { ...form.porDia };
			form.dias.forEach((d) => { porDia[d] = { inicio: form.entrada, fin: form.salida }; });
			cambiar({ distintas: true, porDia });
		} else {
			const primera = sesiones[0];
			cambiar({ distintas: false, entrada: primera ? primera.inicio : form.entrada, salida: primera ? primera.fin : form.salida });
		}
	};
	const cambiarEntrada = (v: string) => {
		if (!form) return;
		const duracion = Math.max(minutos(form.salida) - minutos(form.entrada), 60);
		const salida = minutos(form.salida) <= minutos(v) ? aHHMM(Math.min(minutos(v) + duracion, 1320)) : form.salida;
		cambiar({ entrada: v, salida });
	};

	// ---------------------------------------------------------------- validar en vivo (con pausa)
	const reloj = useRef<number | undefined>(undefined);
	const pedido = useRef(0);
	const validarClave = [sesionesClave, form?.aulaId, form?.maestraId, form?.inicio, form?.nivelId, form?.meses].join("|");
	useEffect(() => {
		if (!form || cargando) return;
		window.clearTimeout(reloj.current);
		setLibres(null);
		// Un choque que devolvió el servidor era de lo capturado antes: al cambiar algo, manda la validación nueva.
		setConflictosServidor([]);
		if (!sesionesOk) { setVal(null); setValidando(false); return; }
		setValidando(true);
		const n = ++pedido.current;
		reloj.current = window.setTimeout(() => {
			validar(campusId, { id: horarioId, aulaId: form.aulaId, maestraId: form.maestraId, inicio: form.inicio, sesiones, nivelId: form.nivelId, meses: form.meses })
				.then((r) => { if (n === pedido.current) { setVal(r); setValidacionCaida(false); setValidando(false); } })
				.catch(() => { if (n === pedido.current) { setVal(null); setValidacionCaida(true); setValidando(false); } });
		}, 400);
		return () => window.clearTimeout(reloj.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validarClave, cargando]);

	// ¿Cambió algo que el servidor revisa (aula, sesiones, fechas, maestra)? Si no, un choque viejo no impide guardar otras cosas.
	const cambioRelevante: boolean = !editando || !orig || !form
		|| form.aulaId !== orig.aulaId || form.maestraId !== orig.maestraId || form.inicio !== orig.inicio
		|| sesionesClave !== JSON.stringify([...orig.sesiones].sort((a, b) => a.dia - b.dia));

	const conflictos: Conflicto[] = val && val.conflictos.length ? val.conflictos : (cambioRelevante ? conflictosServidor : []);
	const choqueBloquea = cambioRelevante && conflictos.length > 0 && !forzar;
	const erroresVal: string[] = val?.errores || [];
	const hora = (val && val.hora) || (sesionesOk ? horaTexto(sesiones) : "");
	const nombre = hora ? hora + (form?.seccion ? " Sección " + form.seccion : "") : "";
	const fin: string | null = val?.fin || (!cambioRelevante ? orig?.fin || null : null);

	const buscarLibres = async () => {
		if (!form) return;
		setCargandoLibres(true); setErrorLibres(null); setLibres(null);
		try {
			const dias = Array.from(new Set(sesiones.map((s) => s.dia)));
			const porDia: { [d: number]: any[] } = {};
			await Promise.all(dias.map(async (d) => { porDia[d] = await ocupacion(campusId, d); }));
			const ocupada = (aulaIdx: number): boolean => sesiones.some((s) => {
				const aula = (porDia[s.dia] || []).find((x) => x.id === aulaIdx);
				return !!aula && aula.bloques.some((b: any) => b.scheduleId !== horarioId && minutos(b.inicio) < minutos(s.fin) && minutos(s.inicio) < minutos(b.fin));
			});
			setLibres(aulas.filter((a) => a.id !== form.aulaId && !ocupada(a.id)));
		} catch (e) {
			setErrorLibres("No se pudo revisar la ocupación de las aulas.");
		} finally {
			setCargandoLibres(false);
		}
	};

	// ---------------------------------------------------------------- reglas por paso
	const periodo: Periodo | null = periodos.find((p) => p.id === form?.periodoId) || null;
	const inicioCambio = !editando || !orig || form?.inicio !== orig.inicio;
	const mesPeriodo = periodo ? periodo.start.slice(0, 7) : "";
	const mesInicio = (form?.inicio || "").slice(0, 7);
	const vence = form ? num(form.venceDia) : NaN;
	const razonPaso = (p: number): string | null => {
		if (!form) return "Cargando…";
		if (p === 0) return form.cursoId && form.nivelId ? null : (form.cursoId ? "Elige el módulo." : "Elige el curso.");
		if (p === 1) {
			if (form.dias.length < 1) return "Elige al menos un día.";
			if (horasMalas) return "La salida debe ser después de la entrada.";
			if (!form.aulaId) return "Elige el aula.";
			if (validando) return "Revisando el horario…";
			if (erroresVal.length) return erroresVal[0];
			if (choqueBloquea) return conflictos[0].tipo === "maestra" ? "Cambia la maestra, el día o la hora para seguir." : "Cambia el aula, el día o la hora para seguir.";
			return null;
		}
		const min = num(form.min), max = num(form.max);
		if (!form.periodoId) return "Elige el periodo.";
		if (!form.inicio) return "Falta la fecha de inicio.";
		if (inicioCambio && mesPeriodo && mesInicio !== mesPeriodo) return "El horario tiene que empezar dentro del mes del periodo (" + (periodo?.name || "") + ").";
		if (cobraPorHorario && !(num(form.meses) >= 1)) return "Faltan los meses a facturar.";
		if (isNaN(min) || min < 1) return "El mínimo de alumnas debe ser al menos 1.";
		if (isNaN(max) || max < min) return "El máximo de alumnas debe ser igual o mayor al mínimo.";
		if (cobraPorHorario && (isNaN(num(form.inscripcion)) || num(form.inscripcion) < 0)) return "Falta el precio de inscripción.";
		if (cobraPorHorario && (isNaN(num(form.mensualidad)) || num(form.mensualidad) < 0)) return "Falta el precio de mensualidad.";
		if (form.ajuste.trim() !== "" && (isNaN(num(form.ajuste)) || Math.abs(num(form.ajuste)) > 99999)) return "El sobrecargo no es válido.";
		if (form.reposicion.trim() !== "" && (isNaN(num(form.reposicion)) || num(form.reposicion) < 0)) return "El precio de reposición no es válido.";
		if (form.inscripcionPropia.trim() !== "" && (isNaN(num(form.inscripcionPropia)) || num(form.inscripcionPropia) < 0)) return "La inscripción propia no es válida.";
		if (isNaN(vence) || vence < 1 || vence > 28) return "El día de vencimiento va de 1 a 28.";
		if (isNaN(num(form.tarifa)) || num(form.tarifa) < 0) return "Falta la tarifa de la maestra.";
		if (validando) return "Revisando el horario…";
		if (erroresVal.length) return erroresVal[0];
		if (choqueBloquea) return conflictos[0].tipo === "maestra" ? "Cambia la maestra, el día o la hora para seguir." : "Cambia el aula, el día o la hora para seguir.";
		return null;
	};
	const razon = razonPaso(paso);

	const irA = (p: number) => {
		setAtras(p < paso); setPaso(p); setErrorGuardar(null);
		setVisitados((v) => (v.includes(p) ? v : [...v, p]));
		window.setTimeout(() => cuerpo.current?.scrollTo({ top: 0 }), 0);
	};

	const alGuardar = async () => {
		if (!form || !nivel || !curso || razonPaso(2) || guardando) return;
		setGuardando(true); setErrorGuardar(null);
		const mesesPlan = plan?.plan?.billableMonths;
		const meses = cobraPorHorario ? form.meses : (mesesPlan ? String(mesesPlan) : form.meses);
		try {
			const r: any = await guardar(campusId, {
				id: horarioId ?? null, cursoOriginal: orig ? orig.cursoId : curso.id, nivelOriginal: orig ? orig.nivelId : nivel.id, nivelId: nivel.id,
				nombre, seccion: form.seccion, notas: form.notas,
				inscripcion: num(form.inscripcion) || 0, mensualidad: num(form.mensualidad) || 0,
				recargoInscripcion: form.recargoInscripcion, recargoMensualidad: form.recargoMensualidad,
				reposicion: form.reposicion.trim(), ajuste: form.ajuste.trim(), tarifaMaestra: form.tarifa, venceDia: form.venceDia, meses,
				max: form.max, min: form.min, periodoId: form.periodoId as number, inicio: form.inicio,
				statusId: orig ? orig.statusId : scheduleStatus.ACTIVE, aulaId: form.aulaId as number, maestraId: form.maestraId, sesiones,
				inscripcionPropia: editando || form.inscripcionPropia.trim() !== "" ? form.inscripcionPropia.trim() : null,
				forzarMotivo: forzar && conflictos.length > 0 ? forzar : null
			});
			const id = Number(r?.data?.id ?? r?.data?.data?.id ?? horarioId ?? 0);
			onGuardado(id);
		} catch (e: any) {
			const s = mensajeServidor(e);
			setErrorGuardar(s.texto || getErrorMessage(e));
			setConflictosServidor(s.conflictos);
			setGuardando(false);
			window.setTimeout(() => cuerpo.current?.scrollTo({ top: 0, behavior: "smooth" }), 0);
		}
	};

	// ---------------------------------------------------------------- piezas
	// Entrada de 7:00 am a 9:30 pm; salida de 7:30 am a 10:00 pm (la sesión va de 7:00 am a 10:00 pm).
	const selectorHora = (valor: string, onChange: (v: string) => void, deshabilitado?: boolean, etiqueta?: string, esSalida?: boolean) => (
		<select className="hf-in" aria-label={etiqueta} value={valor} disabled={deshabilitado} onChange={(e) => onChange(e.target.value)}>
			{(esSalida ? TIEMPOS.slice(1) : TIEMPOS.slice(0, -1)).map((t) => <option key={t} value={t}>{hora12(t)}</option>)}
		</select>
	);
	const edificios = useMemo(() => {
		const m = new Map<number, string>();
		aulas.forEach((a) => m.set(a.edificioId, a.edificio));
		return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
	}, [aulas]);
	const aulasDelEdificio = aulas.filter((a) => form?.edificioId == null || a.edificioId === form.edificioId);
	const mentores = (nivel?.mentores || []).filter((m) => m.activo || m.id === orig?.maestraId);
	const aulaElegida = aulas.find((a) => a.id === form?.aulaId) || null;
	const maestra = mentores.find((m) => m.id === form?.maestraId) || null;

	const avisoChoque = (
		<AvisoChoque
			conflictos={conflictos} esSuper={esSuper} forzar={forzar} onForzar={setForzar}
			onLibres={buscarLibres} libres={libres} cargandoLibres={cargandoLibres} errorLibres={errorLibres}
			onElegirAula={(a) => { cambiar({ aulaId: a.id, edificioId: a.edificioId }); setLibres(null); }}
			sinEdicion={paso === 2 || horasBloqueadas}
		/>
	);

	// ---------------------------------------------------------------- paso 2
	const paso2 = form ? (
		<div>
			{horasBloqueadas ? (
				<div className="hf-nota" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
					<span>Ya tiene listas</span>
					<button type="button" className="hf-boton" onClick={() => setCambiandoHora(true)}>Cambiar Hora</button>
				</div>
			) : null}
			<div className="hf-grupo">
				<span className="hf-rotulo">Días</span>
				<div className="hf-dias" role="group" aria-label="Días">
					{DIAS_CORTOS.map((d, i) => (
						<button type="button" key={d} className={"hf-dia" + (form.dias.includes(i) ? " on" : "")} aria-pressed={form.dias.includes(i)} aria-label={DIAS_LARGOS[i]}
							disabled={horasBloqueadas} onClick={() => alternarDia(i)}>{d}</button>
					))}
				</div>
			</div>
			<div className="hf-grupo">
				<div className="hf-grid tres par">
					<Campo t="Entrada">{selectorHora(form.entrada, cambiarEntrada, horasBloqueadas || form.distintas, "Entrada")}</Campo>
					<Campo t="Salida" falta={horasMalas && !form.distintas}>{selectorHora(form.salida, (v) => cambiar({ salida: v }), horasBloqueadas || form.distintas, "Salida", true)}</Campo>
					<Campo t="Sección">
						<select className="hf-in" value={form.seccion} onChange={(e) => cambiar({ seccion: e.target.value as Seccion })}>
							<option value="">Sin Sección</option>
							<option value="A">Sección A</option>
							<option value="B">Sección B</option>
							<option value="C">Sección C</option>
							{form.seccion && !["A", "B", "C"].includes(form.seccion) ? <option value={form.seccion}>Sección {form.seccion}</option> : null}
						</select>
					</Campo>
				</div>
				{!horasBloqueadas && form.dias.length > 1 ? (
					<div style={{ marginTop: 10 }}>
						<button type="button" className="hf-interruptor" aria-pressed={form.distintas} onClick={alternarDistintas}>
							<FontAwesomeIcon icon={form.distintas ? solid("square-check") : solid("square")} />Horas Distintas por Día
						</button>
					</div>
				) : null}
				{form.distintas && form.dias.length > 1 ? (
					<div className="hf-pordia">
						{[...form.dias].sort().map((d) => {
							const pd = form.porDia[d] || { inicio: form.entrada, fin: form.salida };
							const set = (p: Partial<{ inicio: string; fin: string }>) => cambiar({ porDia: { ...form.porDia, [d]: { ...pd, ...p } } });
							return (
								<React.Fragment key={d}>
									<span>{DIAS_LARGOS[d]}</span>
									{selectorHora(pd.inicio, (v) => set({ inicio: v, fin: minutos(pd.fin) <= minutos(v) ? aHHMM(Math.min(minutos(v) + 180, 1320)) : pd.fin }), horasBloqueadas, "Entrada " + DIAS_LARGOS[d])}
									{selectorHora(pd.fin, (v) => set({ fin: v }), horasBloqueadas, "Salida " + DIAS_LARGOS[d], true)}
								</React.Fragment>
							);
						})}
					</div>
				) : null}
			</div>
			<div className="hf-grid tres hf-grupo">
				<Campo t="Edificio">
					<select className="hf-in" value={form.edificioId ?? ""} onChange={(e) => {
						const id = e.target.value === "" ? null : Number(e.target.value);
						cambiar({ edificioId: id, aulaId: aulas.some((a) => a.id === form.aulaId && (id == null || a.edificioId === id)) ? form.aulaId : null });
					}}>
						<option value="">Todos los Edificios</option>
						{edificios.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
					</select>
				</Campo>
				<Campo t="Aula" falta={!form.aulaId}>
					<select className="hf-in" value={form.aulaId ?? ""} onChange={(e) => {
						const id = e.target.value === "" ? null : Number(e.target.value);
						const a = aulas.find((x) => x.id === id);
						cambiar({ aulaId: id, edificioId: a ? a.edificioId : form.edificioId });
					}}>
						<option value="">Elige el Aula</option>
						{aulasDelEdificio.map((a) => <option key={a.id} value={a.id}>{(form.edificioId == null && a.edificio ? a.edificio + " - " : "") + a.name}</option>)}
					</select>
				</Campo>
				<Campo t="Maestra">
					<select className="hf-in" value={form.maestraId ?? ""} onChange={(e) => cambiar({ maestraId: e.target.value === "" ? null : Number(e.target.value) })}>
						<option value="">{mentores.length ? "Sin Maestra" : "Este Módulo No Tiene Maestras"}</option>
						{mentores.map((m) => <option key={m.id} value={m.id}>{m.name + (m.activo ? "" : " (inactiva)")}</option>)}
					</select>
				</Campo>
			</div>
			<div className="hf-resultado" aria-live="polite">
				{nombre ? <p className="hf-nombre">{nombre}</p> : null}
				{fin ? <div className="hf-fin">Termina el {fechaCorta(fin)}</div> : null}
				{validando && sesionesOk ? <div className="hf-fin">Revisando…</div> : null}
				{validacionCaida && sesionesOk && !validando ? <div className="hf-fin">No se pudo revisar choques ahora; el servidor los revisa al guardar.</div> : null}
				{erroresVal.map((m, i) => <div key={i} className="hf-aviso"><p>{m}</p></div>)}
				{avisoChoque}
			</div>
		</div>
	) : null;

	// ---------------------------------------------------------------- paso 3
	const faltaMes = !!form && !!periodo && inicioCambio && !!form.inicio && mesInicio !== mesPeriodo;
	const dineroInput = (valor: string, onChange: (v: string) => void, opciones: { min?: number; max?: number; placeholder?: string; deshabilitado?: boolean; etiqueta: string }) => (
		<div className="hf-dinero">
			<span>{moneda.symbol}</span>
			<input className="hf-in" type="number" inputMode="decimal" step="0.01" aria-label={opciones.etiqueta} value={valor} min={opciones.min} max={opciones.max}
				placeholder={opciones.placeholder} disabled={opciones.deshabilitado} onChange={(e) => onChange(e.target.value)} />
			<span>{moneda.code}</span>
		</div>
	);
	const textoPlan = !plan ? "…" : (!plan.plan ? "Sin plan de pagos" : plan.plan.billableMonths + " meses × " + dinero(plan.plan.monthlyPrice) + (plan.plan.confirmed ? "" : " (sin confirmar)"));
	const paso3 = form ? (
		<div>
			{errorGuardar ? <div className="hf-error" role="alert">{errorGuardar}</div> : null}
			{conflictos.length > 0 || choqueBloquea ? <div style={{ marginBottom: 14 }}>{avisoChoque}</div> : null}
			<div className="hf-grid hf-grupo">
				<Campo t="Periodo" falta={!form.periodoId}>
					<select className="hf-in" value={form.periodoId ?? ""} disabled={periodoBloqueado} onChange={(e) => {
						const p = periodos.find((x) => x.id === Number(e.target.value));
						cambiar({ periodoId: p ? p.id : null, inicio: p && !inicioBloqueado ? inicioDe(p) : form.inicio });
					}}>
						<option value="">Elige el Periodo</option>
						{periodosElegibles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
					</select>
				</Campo>
				<Campo t="Inicio" falta={!form.inicio || faltaMes}>
					<input className="hf-in" type="date" value={form.inicio} disabled={inicioBloqueado} onChange={(e) => cambiar({ inicio: e.target.value })} />
				</Campo>
				{cobraPorHorario ? (
					<Campo t="Meses a Facturar" falta={!(num(form.meses) >= 1)}>
						<input className="hf-in" type="number" min={1} step={1} value={form.meses} onChange={(e) => cambiar({ meses: e.target.value })} />
					</Campo>
				) : (
					<Campo t="Plan de Pagos"><div className="hf-in" style={{ display: "flex", alignItems: "center", background: "var(--wa-riel)" }}>{textoPlan}</div></Campo>
				)}
				<Campo t="Termina"><div className="hf-in" style={{ display: "flex", alignItems: "center", background: "var(--wa-riel)" }}>{fin ? fechaCorta(fin) : "-"}</div></Campo>
				<Campo t="Mínimo de Alumnas" falta={isNaN(num(form.min)) || num(form.min) < 1}>
					<input className="hf-in" type="number" min={1} step={1} value={form.min} onChange={(e) => cambiar({ min: e.target.value })} />
				</Campo>
				<Campo t="Máximo de Alumnas" falta={isNaN(num(form.max)) || num(form.max) < num(form.min)}>
					<input className="hf-in" type="number" min={1} step={1} value={form.max} onChange={(e) => cambiar({ max: e.target.value })} />
				</Campo>
				{cobraPorHorario ? (
					<>
						<Campo t="Precio de Inscripción">{dineroInput(form.inscripcion, (v) => cambiar({ inscripcion: v }), { min: 0, etiqueta: "Precio de inscripción" })}</Campo>
						<Campo t="Precio de Mensualidad">{dineroInput(form.mensualidad, (v) => cambiar({ mensualidad: v }), { min: 0, etiqueta: "Precio de mensualidad" })}</Campo>
					</>
				) : (
					<>
						<Campo t="Sobrecargo del Horario (Opcional)">{dineroInput(form.ajuste, (v) => cambiar({ ajuste: v }), { min: -99999, max: 99999, etiqueta: "Sobrecargo del horario" })}</Campo>
						<Campo t="Inscripción Propia (Opcional)">{dineroInput(form.inscripcionPropia, (v) => cambiar({ inscripcionPropia: v }), { min: 0, max: 999999, etiqueta: "Inscripción propia" })}</Campo>
					</>
				)}
				<Campo t="Precio de Reposición (Opcional)">{dineroInput(form.reposicion, (v) => cambiar({ reposicion: v }), { min: 0, max: 99999, etiqueta: "Precio de reposición" })}</Campo>
				<Campo t="Día de Vencimiento" falta={isNaN(vence) || vence < 1 || vence > 28}>
					<input className="hf-in" type="number" min={1} max={28} step={1} value={form.venceDia} onChange={(e) => cambiar({ venceDia: e.target.value })} />
				</Campo>
				<Campo t="Tarifa de la Maestra" falta={isNaN(num(form.tarifa)) || num(form.tarifa) < 0}>
					{dineroInput(form.tarifa, (v) => cambiar({ tarifa: v }), { min: 0, etiqueta: "Tarifa de la maestra" })}
				</Campo>
				<Campo t="Notas" ancho>
					<textarea className="hf-in" maxLength={255} value={form.notas} onChange={(e) => cambiar({ notas: e.target.value })} />
				</Campo>
			</div>
			<div className="hf-tarjeta">
				<h5>{nombre || "-"}</h5>
				<dl className="hf-resumen">
					<dt>Curso</dt><dd>{curso?.name} {nivel ? " - " + nivel.name : ""}</dd>
					<dt>Aula</dt><dd>{aulaElegida ? (aulaElegida.edificio ? aulaElegida.edificio + ", " : "") + aulaElegida.name : "-"}</dd>
					<dt>Maestra</dt><dd>{maestra ? maestra.name : "Sin maestra"}</dd>
					<dt>Fechas</dt><dd>{form.inicio ? fechaCorta(form.inicio) : "-"} al {fin ? fechaCorta(fin) : "-"}</dd>
					<dt>Alumnas</dt><dd>{form.min} a {form.max}</dd>
					<dt>{cobraPorHorario ? "Mensualidad" : "Plan de Pagos"}</dt><dd>{cobraPorHorario ? dinero(num(form.mensualidad)) + (num(form.meses) ? " × " + form.meses + " meses" : "") : textoPlan}</dd>
				</dl>
			</div>
		</div>
	) : null;

	// ---------------------------------------------------------------- armado
	const titulo = editando ? "Editar Horario" : "Nuevo Horario";
	const avance = ((paso + 1) / PASOS.length) * 100;
	const ultimo = paso === PASOS.length - 1;

	return (
		<>
		<Modal className="waModulo waPasos hf" show onHide={guardando ? undefined : onCerrar} size="lg" centered scrollable backdrop="static" fullscreen="sm-down" enforceFocus={false}>
			<Modal.Header closeButton={!guardando} style={{ flexDirection: "column", alignItems: "stretch", gap: 10, position: "relative" }}>
				<Modal.Title style={{ fontSize: 18, paddingRight: 32 }}>
					{titulo}{editando && orig ? <span style={{ fontSize: 14, fontWeight: 500, color: "var(--wa-apagado)", marginLeft: 8 }}>{orig.nombre}</span> : null}
				</Modal.Title>
				<div className="waAvance" aria-label={"Paso " + (paso + 1) + " de " + PASOS.length}>
					<div className="waAvanceBarra"><div style={{ width: avance + "%" }} /></div>
					<div className="waAvancePasos">
						{PASOS.map((p, i) => {
							const clase = i === paso ? "actual" : (visitados.includes(i) && i < paso && !razonPaso(i) ? "listo" : visitados.includes(i) ? "visto" : "");
							return (
								<button type="button" key={p.key} className={"waAvancePaso " + clase} disabled={guardando || !visitados.includes(i) || (i > paso && !!razonPaso(paso))} onClick={() => irA(i)}>
									<span className="waAvancePunto">{clase === "listo" ? <FontAwesomeIcon icon={solid("check")} /> : i + 1}</span>
									<span className="waAvanceNombre">{p.titulo}</span>
								</button>
							);
						})}
					</div>
				</div>
			</Modal.Header>
			<Modal.Body ref={cuerpo as any} style={{ textAlign: "left", fontSize: 16 }}>
				<div className="hf-cuerpo">
					{cargando ? <div className="hf-cargando">Cargando…</div> : errorCarga ? <div className="hf-error" role="alert">{errorCarga}</div> : form ? (
						<div key={paso} className={"waPaso " + (atras ? "atras" : "adelante")}>
							<div className="hf-cabeza"><h4>{PASOS[paso].titulo}</h4></div>
							{paso === 0 ? <Paso1 form={form} cat={cat} curso={curso} nivel={nivel} cambiando={cambiandoCurso} nivelBloqueado={nivelBloqueado} onCambiar={() => setCambiandoCurso(true)} onCurso={(c) => { cambiar({ cursoId: c.id, nivelId: null }); if (c.niveles.length === 1) elegirNivel(c, c.niveles[0]); }} onNivel={elegirNivel} /> : paso === 1 ? paso2 : paso3}
						</div>
					) : null}
				</div>
			</Modal.Body>
			<Modal.Footer className="waPasosPie hf-pie">
				{razon && !cargando && form ? <div className="hf-razon" role="status">{razon}</div> : null}
				<button type="button" className="btn btn-link waGuardar hf-cancelar" disabled={guardando} onClick={onCerrar}>Cancelar</button>
				<div className="waPasosBotones">
					{paso > 0 ? <button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={() => irA(paso - 1)}><FontAwesomeIcon icon={solid("arrow-left")} />&nbsp;Atrás</button> : null}
					{ultimo
						? <button type="button" className="btn btn-primary" disabled={guardando || !!razon || cargando || !form} onClick={alGuardar}><FontAwesomeIcon icon={solid("floppy-disk")} />&nbsp;{guardando ? "Guardando…" : "Guardar"}</button>
						: <button type="button" className="btn btn-primary" disabled={!!razon || cargando || !form} onClick={() => irA(paso + 1)}>Siguiente&nbsp;<FontAwesomeIcon icon={solid("arrow-right")} /></button>}
				</div>
			</Modal.Footer>
		</Modal>
		{cambiandoHora && orig && horarioId != null ? (
			<CambiarHora
				campusId={campusId} horarioId={horarioId} titulo={orig.nombre} sesionesIniciales={orig.sesiones} editable
				onClose={() => setCambiandoHora(false)} onHecho={() => { setCambiandoHora(false); onGuardado(horarioId); }}
			/>
		) : null}
		</>
	);
};

const HorarioForm: React.FC<HorarioFormProps> = (props) => (props.show ? <HorarioFormInterior key={String(props.horarioId ?? "nuevo") + ":" + String(props.nivelId ?? "") + ":" + String(props.aulaId ?? "") + ":" + String(props.weekDay ?? "")} {...props} /> : null);

export default HorarioForm;
